import * as Yup from 'yup';

import { useState } from 'react';

import { Formik } from 'formik';
import { Link as RouterLink } from 'react-router-dom';

import {
  Box,
  Button,
  FormHelperText,
  TextField,
  Checkbox,
  Typography,
  Link,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Alert
} from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import useRefMounted from 'src/hooks/useRefMounted';
import { useTranslation } from 'react-i18next';

import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import toast from 'react-hot-toast';

const LoginJWT = () => {
  const { login } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const isMountedRef = useRefMounted();
  const { t } = useTranslation();

  const toggleShowPassword = () => setShowPassword((prevState) => !prevState);

  return (
    <Formik
      initialValues={{
        username: '',
        password: '',
        terms: false,
        privacy: false,
        productUpdates: false,
        submit: null
      }}
      validationSchema={Yup.object().shape({
        username: Yup.string()
          .max(255)
          .required(t('Enter phone number or email')),
        password: Yup.string().max(255).required(t('Password is required')),
        terms: Yup.boolean().oneOf(
          [true],
          t('You must accept the Terms of Use')
        ),
        privacy: Yup.boolean().oneOf(
          [true],
          t('You must accept the Privacy Notice to continue')
        ),
        productUpdates: Yup.boolean()
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        const TOAST_ID = 'login_toast';
        try {
          const emailRe = /^\w+@[a-zA-Z_]+?\.[a-zA-Z]{2,3}$/;
          let { username, password } = values;
          const isEmail = emailRe.test(username);
          username =
            !isEmail && !username.includes('+91') ? `+91${username}` : username;

          const purposes = [
            'account management',
            'SIM and device connectivity',
            'location tracking of my devices',
            'billing'
          ];
          if (values.productUpdates) {
            purposes.push('product updates by email');
          }

          await login(username, password, TOAST_ID, {
            termsAccepted: values.terms,
            privacyAccepted: values.privacy,
            purposes
          });

          if (isMountedRef.current) {
            setStatus({ success: true });
            setSubmitting(false);
          }
        } catch (err) {
          console.error(err);

          toast.error(err?.message || 'Could not login', { id: TOAST_ID });

          if (isMountedRef.current) {
            setStatus({ success: false });
            setErrors({ submit: err?.message || 'Could not login' });
            setSubmitting(false);
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          {/* <Message /> */}
          <TextField
            error={Boolean(touched.username && errors.username)}
            fullWidth
            margin="normal"
            autoFocus
            helperText={touched.username && errors.username}
            label={t('Email or Phone Number')}
            name="username"
            onBlur={handleBlur}
            onChange={handleChange}
            type="username"
            value={values.username}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.password && errors.password)}
            fullWidth
            margin="normal"
            helperText={touched.password && errors.password}
            label={t('Password')}
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            type={showPassword ? 'text' : 'password'}
            value={values.password}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <IconButton onClick={toggleShowPassword}>
                  {showPassword ? (
                    <VisibilityOffRoundedIcon />
                  ) : (
                    <VisibilityRoundedIcon />
                  )}
                </IconButton>
              )
            }}
          />
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ mt: 1, mb: 0.5 }}
          >
            <Link component={RouterLink} to="/auth/forgot-password">
              <b>{t('Lost password?')}</b>
            </Link>
          </Box>

          <Box display="flex" flexDirection="column" sx={{ mt: 0.5, gap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.terms}
                  name="terms"
                  color="primary"
                  onChange={handleChange}
                />
              }
              label={
                <Typography variant="body2">
                  {t('I accept the')}{' '}
                  <Link
                    component="a"
                    href="/public/term-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('Terms of Use')}
                  </Link>
                </Typography>
              }
            />
            {Boolean(touched.terms && errors.terms) && (
              <FormHelperText error sx={{ ml: 4, mt: -0.5 }}>
                {errors.terms}
              </FormHelperText>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={values.privacy}
                  name="privacy"
                  color="primary"
                  onChange={handleChange}
                />
              }
              label={
                <Typography variant="body2">
                  {t('I have read the')}{' '}
                  <Link
                    component="a"
                    href="/public/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('Privacy Notice')}
                  </Link>{' '}
                  {t(
                    'and consent to Ventel processing my personal data for: account management, SIM and device connectivity, location tracking of my devices, and billing.'
                  )}
                </Typography>
              }
            />
            {Boolean(touched.privacy && errors.privacy) && (
              <FormHelperText error sx={{ ml: 4, mt: -0.5 }}>
                {errors.privacy}
              </FormHelperText>
            )}

            {/* <FormControlLabel
              control={
                <Checkbox
                  checked={values.productUpdates}
                  name="productUpdates"
                  color="primary"
                  onChange={handleChange}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  {t(
                    '(Optional) I consent to receive product updates and service communications by email.'
                  )}
                </Typography>
              }
            /> */}
          </Box>

          {Boolean(errors.submit) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.submit}
            </Alert>
          )}

          <Button
            sx={{
              mt: 3
            }}
            color="primary"
            startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
            disabled={isSubmitting || !values.terms || !values.privacy}
            type="submit"
            fullWidth
            size="large"
            variant="contained"
          >
            {t('Sign in')}
          </Button>
        </form>
      )}
    </Formik>
  );
};

export default LoginJWT;
